import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import TableIdButton from "@lib/components/MiniComponents/Buttons/TableIdButton";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { booleanColor, userRoleColor } from "@lib/helpers/ColorHelper";
import { shortenString } from "@lib/helpers/StringHelper";
import { ColumnConfig, generateColumns } from "@lib/helpers/TableHelper";
import { UseMutationProps } from "@lib/types/Api";
import { Manager, ManagerGroupsEnum } from "@lib/types/Manager";
import { Tag } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { getAvailableRoles } from "@lib/helpers/RoleHelper";
import { AuthRoles } from "@lib/types/Auth";
import { TableColumnsProps } from "@lib/types/General";
import { getRoute } from "@lib/helpers/RouteHelper";
import { ROUTES } from "@lib/constants";
import {
  CustomerName,
  ManagerName,
  ProviderName,
} from "@lib/components/MiniComponents/Others";
import { AppContext } from "@lib/types";

type Props<T> = TableColumnsProps<T> & {
  openModal: boolean;
  handleSelectManager: (manager: Manager) => void;
  deleteQuery: UseMutationProps<Manager>;
  userRole?: AuthRoles;
  group?: ManagerGroupsEnum;
};

const getColumns = <T extends Manager>(props: Props<T>): ColumnsType<T> => {
  const {
    deleteQuery,
    handleSelectManager,
    apiData,
    messages,
    userRole,
    context,
    group,
  } = props;

  // Get available roles for the current user
  const availableRoles = userRole
    ? getAvailableRoles(userRole)
    : Object.values(AuthRoles);

  // Filter roles based on user's permissions and available data
  const roles = [...new Set(apiData?.map((d) => d.role))].filter((role) =>
    availableRoles.includes(role as AuthRoles),
  );

  const columnsConfig: ColumnConfig<T>[] = [
    {
      title: "ID",
      dataIndex: "id",
      render: (v, r) => (
        <TableIdButton
          // link={`/payment_history/list/${r.id}`}
          text={`#${shortenString(r.id)}`}
          textToBeCopied={r.id}
        />
      ),
      width: "180px",
      filterType: "search",
    },
    {
      title: "common.fullName",
      dataIndex: "firstName",
      width: "180px",
      render: (v, r) => <ManagerName manager={r} link />,
      filterType: "search",
    },
    {
      title: "common.customer",
      dataIndex: "customer.id",
      key: "customer.name",
      width: "180px",
      render: (v, r) => <CustomerName customer={r.customer} link />,
      hidden:
        (!context || context !== AppContext.CUSTOMER) &&
        (!group || group === ManagerGroupsEnum.ADMIN),
    },
    {
      title: "common.provider",
      dataIndex: "provider.name",
      width: "180px",
      render: (v, r) => <ProviderName provider={r.provider} link />,
      filterType: "search",
      hidden:
        (!context || context !== AppContext.PAYMENT_PROVIDER) &&
        (!group || group === ManagerGroupsEnum.ADMIN),
    },
    {
      title: "common.role",
      dataIndex: "role",
      width: "130px",
      render: (role) => <Tag color={userRoleColor(role)}>{role}</Tag>,
      filters: roles.map((role) => ({
        text: <Tag color={userRoleColor(role)}>{role}</Tag>,
        value: role,
      })),
      filterType: "dropdown",
    },
    {
      title: "common.isManagement",
      dataIndex: "isManagement",
      width: "180px",
      render: (value) => (
        <Tag color={booleanColor(value)}>
          {
            (value
              ? messages["common.enabled"]
              : messages["common.disabled"]) as string
          }
        </Tag>
      ),
      filterType: "dropdown",
      hidden: userRole !== AuthRoles.Admin,
    },
    {
      title: "common.twoFactor",
      dataIndex: "isTwoFactorAuthEnabled",
      width: "180px",
      render: (value) => (
        <Tag color={booleanColor(value)}>
          {
            (!!value
              ? messages["common.enabled"]
              : messages["common.disabled"]) as string
          }
        </Tag>
      ),
      filters: [
        {
          text: messages["common.enabled"] as string,
          value: true,
        },
        {
          text: messages["common.disabled"] as string,
          value: false,
        },
      ],
      filterType: "dropdown",
    },
    {
      title: "common.createdAt",
      dataIndex: "createdAt",
      width: "180px",
      render: (d) => dayjs(d).format("DD/MM/YYYY"),
    },
    {
      title: "common.createdBy",
      dataIndex: "createdBy",
      width: "180px",
      filterType: "dropdown",
    },
    {
      title: "common.updatedAt",
      dataIndex: "updatedAt",
      width: "180px",
      render: (d) => dayjs(d).format("DD/MM/YYYY"),
    },
    {
      title: "common.updatedBy",
      dataIndex: "updatedBy",
      width: "180px",
      filterType: "dropdown",
    },
    {
      title: "common.actions",
      dataIndex: "actions",
      className: "order-table-action",
      fixed: "right",
      width: "110px",
      render: (_, record) => (
        <StyledFlex className="actions">
          <ActionButton
            type="edit"
            onClick={() => handleSelectManager(record)}
            noLabel
          />

          <ActionButton
            type="detail"
            href={getRoute(ROUTES.MANAGER.DETAIL, {
              managerId: record.id,
            })}
            noLabel
          />

          <ActionButton
            type="delete"
            onClick={() => deleteQuery.mutate(record)}
            noLabel
            itemName="common.manager"
          />
        </StyledFlex>
      ),
    },
  ];

  return generateColumns(props, columnsConfig);
};

export default getColumns;
